/**
 * Report Templates — frontend API client (FEAT-2026-0005, Phase 2).
 *
 * Thin wrappers around /api/report-templates/* — same shape as the backend
 * controller. Admin-only endpoints; the AppShell already gates the
 * /admin/report-templates route to admin+ users.
 *
 * Phase 6: `saveVersion` now requires `expectedBaseVersion` and converts
 * the backend's 409 STALE_VERSION response into a typed `StaleVersionError`
 * so callers can `instanceof`-check it and render a useful UX (instead of
 * relying on `err.response.status === 409` everywhere).
 */

import { AxiosError } from 'axios';
import apiClient from './apiClient';

// ── Types mirrored from the backend (kept loose to avoid coupling
//    to the backend's internal Zod-inferred types). ─────────────────────────

export interface ReportTemplateSummary {
  ReportTemplateKey: string;
  TestKey: string;
  TestName: string;
  Name: string;
  CurrentVersionNumber: number | null;
  CurrentVersionPublishedAt: string | null;
  CurrentVersionPublishedBy: string | null;
  TotalVersions: number;
}

export interface ReportTemplateRow {
  ReportTemplateKey: string;
  TestKey: string;
  Name: string;
  CurrentVersionKey: string | null;
  IsBuiltin: boolean;
}

export interface ReportTemplateVersionRow {
  ReportTemplateVersionKey: string;
  ReportTemplateKey: string;
  VersionNumber: number;
  Config: string;
  ConfigHash: string;
  ChangeNote: string | null;
  ParentVersionKey: string | null;
  PublishedAt: string;
  PublishedBy: string;
  IsFactory: boolean;
}

export interface VersionWithMetadata {
  version: ReportTemplateVersionRow;
  parsedConfig: unknown;
  isValid: boolean;
}

export interface PreviewSampleRow {
  ReportTemplatePreviewSampleKey: string;
  TestKey: string;
  WorkOrderDetailKey: string;
  Label: string;
  Description: string | null;
  DisplayOrder: number;
  IsBuiltin: boolean;
  // Optional context joined in by the backend (used to disambiguate when
  // two samples share the same Label).
  ProjectId?: string | null;
  ProjectName?: string | null;
  SampleReference?: string | null;
  WorkOrderId?: string | null;
  /**
   * Source test type. Always populated by the cross-test endpoint
   * (`findAllAcrossTests`), used by the Shared Header & Footer template
   * so the dropdown makes the test type obvious. Optional for compatibility
   * with the per-test endpoint.
   */
  TestName?: string | null;
}

/**
 * Short, friendly test-name prefix for the cross-test sample dropdown.
 * Keeps the label short ("Atterberg" vs "Atterberg Limits") so the dropdown
 * stays readable.
 */
function shortTestName(name: string | null | undefined): string {
  if (!name) return '';
  if (name === 'Atterberg Limits') return 'Atterberg';
  if (name === 'Particle Size Distribution') return 'PSD';
  if (name === 'Grain Size Analysis') return 'PSD';
  if (name === 'Direct Shear Test') return 'Direct Shear';
  return name;
}

/**
 * Build a unique display label for a preview sample.
 *
 * Detection: if the sample list spans MULTIPLE test types (the Shared
 * Header & Footer case), every option is prefixed with the test name —
 * "Atterberg: Typical sample", "PSD: Sieve-only sample" — so the user
 * always knows which test their preview will render. In single-test mode
 * the prefix is omitted (it'd be redundant) and the existing label-collision
 * disambiguation (sample reference / work order / project) kicks in.
 */
export function formatSampleOption(s: PreviewSampleRow, allRows: PreviewSampleRow[]): string {
  const distinctTests = new Set(
    allRows.map((r) => r.TestName).filter((n): n is string => !!n),
  );
  const isCrossTest = distinctTests.size > 1;

  if (isCrossTest && s.TestName) {
    const sameLabel = allRows.filter((r) => r.Label === s.Label && r.TestName === s.TestName);
    if (sameLabel.length <= 1) return `${shortTestName(s.TestName)}: ${s.Label}`;
    const ctx = s.SampleReference || s.WorkOrderId || s.ProjectId
      || s.WorkOrderDetailKey.slice(0, 8);
    return `${shortTestName(s.TestName)}: ${s.Label} · ${ctx}`;
  }

  const sameLabel = allRows.filter((r) => r.Label === s.Label);
  if (sameLabel.length <= 1) return s.Label;
  const ctx = s.SampleReference || s.WorkOrderId || s.ProjectId
    || s.WorkOrderDetailKey.slice(0, 8);
  return `${s.Label} · ${ctx}`;
}

// ── Errors ──────────────────────────────────────────────────────────────────

/**
 * Thrown by `saveVersion` when the backend rejects the save with
 * 409 STALE_VERSION — another admin published a new version while the
 * caller was editing. The `payload` mirrors the backend's response body
 * and has everything the UI needs to explain the situation:
 *   • currentVersionNumber  — what's now published on the server
 *   • currentVersionPublishedBy / At — who/when, for the snackbar copy
 *   • expectedBaseVersion   — the stale version the user was editing on
 */
export class StaleVersionError extends Error {
  readonly isStaleVersion = true;
  constructor(
    public readonly payload: {
      expectedBaseVersion: number | null;
      currentVersionNumber: number | null;
      currentVersionPublishedBy: string | null;
      currentVersionPublishedAt: string | null;
      message: string;
    },
  ) {
    super(payload.message);
    this.name = 'StaleVersionError';
  }
}

// ── Endpoints ───────────────────────────────────────────────────────────────

export const reportTemplateService = {
  /** GET /api/report-templates */
  async list(): Promise<ReportTemplateSummary[]> {
    const r = await apiClient.get<ReportTemplateSummary[]>('/report-templates');
    return r.data;
  },

  /** GET /api/report-templates/:key */
  async get(templateKey: string): Promise<{ template: ReportTemplateRow; currentVersion: VersionWithMetadata | null }> {
    const r = await apiClient.get(`/report-templates/${templateKey}`);
    return r.data;
  },

  /** GET /api/report-templates/:key/schema — JSON Schema for RJSF */
  async getSchema(templateKey: string): Promise<unknown> {
    const r = await apiClient.get(`/report-templates/${templateKey}/schema`);
    return r.data;
  },

  /** GET /api/report-templates/:key/factory — schema-registered factory defaults */
  async getFactory(templateKey: string): Promise<unknown> {
    const r = await apiClient.get(`/report-templates/${templateKey}/factory`);
    return r.data;
  },

  /** GET /api/report-templates/:key/versions */
  async listVersions(templateKey: string): Promise<VersionWithMetadata[]> {
    const r = await apiClient.get<VersionWithMetadata[]>(`/report-templates/${templateKey}/versions`);
    return r.data;
  },

  /** GET /api/report-templates/:key/versions/:n */
  async getVersion(templateKey: string, versionNumber: number): Promise<VersionWithMetadata> {
    const r = await apiClient.get<VersionWithMetadata>(`/report-templates/${templateKey}/versions/${versionNumber}`);
    return r.data;
  },

  /**
   * POST /api/report-templates/:key/versions
   *
   * Phase 6 — `expectedBaseVersion` is REQUIRED. Pass the version number
   * the user's draft was based on (the `currentVersion.VersionNumber`
   * loaded into the editor), or `null` if you're publishing v1 against a
   * template that has never been published. Throws `StaleVersionError`
   * (with the freshly-published version's metadata in `payload`) when
   * another admin published while the caller was editing.
   */
  async saveVersion(
    templateKey: string,
    body: {
      config: unknown;
      changeNote?: string | null;
      expectedBaseVersion: number | null;
    },
  ): Promise<VersionWithMetadata> {
    try {
      const r = await apiClient.post<VersionWithMetadata>(`/report-templates/${templateKey}/versions`, body);
      return r.data;
    } catch (err) {
      // Translate 409 STALE_VERSION into a typed error so the caller can
      // pattern-match without poking at axios internals.
      if (err instanceof AxiosError
          && err.response?.status === 409
          && err.response.data?.error === 'STALE_VERSION') {
        const d = err.response.data as {
          expectedBaseVersion: number | null;
          currentVersionNumber: number | null;
          currentVersionPublishedBy: string | null;
          currentVersionPublishedAt: string | null;
          message?: string;
        };
        throw new StaleVersionError({
          expectedBaseVersion: d.expectedBaseVersion,
          currentVersionNumber: d.currentVersionNumber,
          currentVersionPublishedBy: d.currentVersionPublishedBy,
          currentVersionPublishedAt: d.currentVersionPublishedAt,
          message: d.message ?? 'Another admin published while you were editing.',
        });
      }
      throw err;
    }
  },

  /** POST /api/report-templates/:key/restore/:n */
  async restore(templateKey: string, versionNumber: number): Promise<VersionWithMetadata> {
    const r = await apiClient.post<VersionWithMetadata>(`/report-templates/${templateKey}/restore/${versionNumber}`);
    return r.data;
  },

  /** GET /api/report-templates/:key/diff/:a/:b */
  async getDiff(templateKey: string, a: number, b: number): Promise<{ a: VersionWithMetadata; b: VersionWithMetadata }> {
    const r = await apiClient.get(`/report-templates/${templateKey}/diff/${a}/${b}`);
    return r.data;
  },

  /** GET /api/report-templates/:key/samples */
  async listSamples(templateKey: string): Promise<PreviewSampleRow[]> {
    const r = await apiClient.get<PreviewSampleRow[]>(`/report-templates/${templateKey}/samples`);
    return r.data;
  },

  /** POST /api/report-templates/:key/preview — returns a Blob with the PDF. */
  async renderPreview(
    templateKey: string,
    body: { config: unknown; workOrderDetailKey: string },
  ): Promise<Blob> {
    const r = await apiClient.post(`/report-templates/${templateKey}/preview`, body, {
      responseType: 'blob',
    });
    return r.data as Blob;
  },
};
